/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable class-methods-use-this,@typescript-eslint/ban-types,@typescript-eslint/no-unused-vars */
import i18next, {
  BackendModule,
  InitOptions,
  ReadCallback,
  Services,
} from "i18next";
import { initReactI18next } from "react-i18next";
import * as Sentry from "@sentry/react";
import { Storage } from "../utils/storage";

class BundledBackend implements BackendModule {
  type: "backend";

  constructor() {
    this.type = "backend";
  }

  read(language: string, namespace: string, callback: ReadCallback): void {
    import(`../assets/locales/${language}/${namespace}.json`)
      .then((resources) => {
        callback(null, resources);
      })
      .catch((error) => {
        callback(error, null);
      });
  }

  init(
    services: Services,
    backendOptions: object,
    i18nextOptions: InitOptions
  ): void {}
}

const bundledBackend = new BundledBackend();

const isBreadcrumbForReplacement = (key: string) => {
  return (
    key.includes("breadcrumb.") &&
    /[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/gi.test(
      key
    )
  );
};

i18next
  .use(initReactI18next)
  .use(bundledBackend)
  .init({
    ns: [
      "2fa",
      "admin",
      "billing",
      "common",
      "configuration",
      "form",
      "hl7",
      "login",
      "modal",
      "monitor",
      "navbar",
      "rule",
      "studies",
      "table",
      "upload",
    ],
    defaultNS: "common",
    fallbackNS: "common",
    lng: JSON.parse(
      Storage.local.get("locale", window.navigator.language) as string
    ),
    interpolation: {
      escapeValue: false,
    },
    debug: import.meta.env.NODE_ENV === "development",
    fallbackLng: "en",
    saveMissing: true,
    missingKeyHandler: (lngs, ns, key, fallbackValue) => {
      if (isBreadcrumbForReplacement(key)) {
        // These get replaced and are not shown to the user
        return;
      }
      const message = `Missing translation: ${key} from namespace: ${ns} for language: ${lngs}`;
      if (import.meta.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error(message);
      } else {
        Sentry.captureMessage(message);
      }
    },
  });

export default i18next;
